/**
 * Lightweight PostHog wrappers.
 * PostHog is loaded as `window.posthog` from index.html.
 * Captures noop quietly when PH isn't initialised.
 */

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

/** Browser-derived locale segmentation properties. Auto-attached to every event. */
function _localeProps() {
  if (typeof window === "undefined") return {};
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    const lang = navigator.language || null;
    const langCode = lang ? lang.split("-")[0].toLowerCase() : null;
    return {
      tz,
      locale: lang,
      language_code: langCode,
    };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] locale detection failed:", e?.message);
    return {};
  }
}

/** Pull UTM-style params off `location.search`. */
export function readUTM(search = (typeof window !== "undefined" ? window.location.search : "")) {
  const out = {};
  try {
    const sp = new URLSearchParams(search);
    UTM_KEYS.forEach((k) => {
      const v = sp.get(k);
      if (v) out[k] = v;
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] UTM parse failed:", e?.message);
  }
  return out;
}

/** Capture a PostHog event with merged UTM + locale properties. */
export function capture(event, props = {}) {
  if (typeof window === "undefined" || !window.posthog?.capture) return;
  const utm = readUTM();
  const locale = _localeProps();
  try {
    window.posthog.capture(event, { ...locale, ...utm, ...props });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] capture failed:", event, e?.message);
  }
}

/** Identify a user with PostHog. Called once on auth resolve.
 * Extra `props` typically include subscription_plan, country, currency, etc.
 * — supports analytics segmentation across plan / country / currency / language. */
export function identify(userId, props = {}) {
  if (typeof window === "undefined" || !window.posthog?.identify) return;
  try {
    window.posthog.identify(userId, { ..._localeProps(), ...props });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] identify failed:", e?.message);
  }
}

/** Convenience: capture only when at least one UTM param is present. */
export function captureNudgeArrival(event, extra = {}) {
  const utm = readUTM();
  if (!utm.utm_source) return;
  capture(event, { ...extra, ...utm });
}
